import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom"; // Importamos o Link
import { useLang } from "@/context/LanguageContext";
import { Menu, X, ChevronDown } from "lucide-react";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Nova estrutura usando "path" para definir a rota da página
  const navItems = [
    { 
      key: "tours", 
      path: "/shop",
      subItems: [
        { key: "rivas_perezzeledon", path: "/rivas-perezzeledon", label: "Rivas." },
        { key: "dominicalito_dominical", path: "/dominicalito-dominical", label: "Dominicalito." },
        { key: "oasis_huacachina_peru", path: "/oasis-huacachina-peru", label: "Huacachina, Peru." },
        { key: "uvita_whaletail", path: "/uvita-whaletail", label: "Dominical." },
        { key: "caraguatatuba", path: "/caraguatatuba", label: "Litoral Norte SP, Brasil." },

        //{ key: "solo", path: "/solo-course", label: "Solo Course" },
      ]
    },
    { 
      key: "destinations", 
      path: "/destinations",
      subItems: [
        { key: "costarica", path: "/costarica", label: "Costa Rica." },
        { key: "peru", path: "/peru", label: "Peru."},
        { key: "canada", path: "/tandem/flywithmac", label: "Canada." },
        { key: "argentina", path: "/shop", label: "Argentina." },
        { key: "brasil", path: "/brasil", label: "Brazil." },
      ]
    },
    { key: "gallery", path: "/gallery" },
    { key: "faq", path: "/faq" },
  ];

  // Função para fechar os menus ao clicar num link
  const closeMenus = () => {
    setMobileOpen(false);
    setOpenMobileMenu(null);
  };

  const toggleMobileSubmenu = (e, key) => {
    e.preventDefault();
    setOpenMobileMenu(openMobileMenu === key ? null : key);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome ? "backdrop-blur-xl bg-white/80 border-b border-jungle" : "bg-white/80"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          onClick={closeMenus}
          data-testid="logo-link"
          className="font-display text-xl md:text-2xl text-jungle tracking-tight leading-none"
        >
          PARAGLIDE<span className="text-terracotta">.</span>TOUR
        </Link>

        {/* Navegação Desktop */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map(item => (
            <div key={item.key} className="relative group">
              <Link
                to={item.path}
                onClick={closeMenus}
                data-testid={`nav-${item.key}`}
                className="flex items-center gap-1 font-overline text-xs text-jungle hover:text-terracotta transition-colors py-2"
              >
                {t.nav[item.key]} 
                {item.subItems && (
                  <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
                )}
              </Link>
              
              {/* Dropdown Desktop */}
              {item.subItems && (
                <div className="absolute top-full left-0 w-48 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-white shadow-xl border border-jungle/10 rounded-md py-2 flex flex-col">
                    {item.subItems.map(sub => (
                      <Link
                        key={sub.key}
                        to={sub.path}
                        onClick={closeMenus}
                        className="px-4 py-2 font-overline text-xs text-jungle hover:bg-jungle/5 hover:text-terracotta transition-colors"
                      >
                        {t.nav[sub.key] || sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Utilitários Header (Idioma + Botão) */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 border border-jungle rounded-sm p-0.5" data-testid="language-switcher">
            {["en", "es", "pt"].map(l => (
              <button
                key={l}
                data-testid={`lang-btn-${l}`}
                onClick={() => setLang(l)}
                className={`px-2 py-1 text-xs font-medium uppercase tracking-widest transition-colors ${
                  lang === l ? "bg-jungle text-sand" : "text-jungle hover:bg-jungle/10"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <Link
            to="/book"
            onClick={closeMenus}
            data-testid="header-book-btn"
            className="hidden md:inline-flex btn-primary"
            style={{padding: "10px 20px", fontSize: "12px"}}
          >
            {t.nav.book}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-jungle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Navegação Mobile */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-jungle">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map(item => (
              <div key={item.key} className="flex flex-col">
                <div className="flex justify-between items-center">
                  <Link
                    to={item.path}
                    onClick={closeMenus}
                    data-testid={`mobile-nav-${item.key}`}
                    className="font-overline text-sm text-jungle flex-grow"
                  >
                    {t.nav[item.key]}
                  </Link>
                  {item.subItems && (
                    <button 
                      onClick={(e) => toggleMobileSubmenu(e, item.key)}
                      className="p-1 text-jungle"
                    >
                      <ChevronDown 
                        size={18} 
                        className={`transition-transform duration-200 ${openMobileMenu === item.key ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                </div>
                
                {/* Dropdown Mobile */}
                {item.subItems && openMobileMenu === item.key && (
                  <div className="flex flex-col gap-3 mt-3 pl-4 border-l-2 border-jungle/10">
                    {item.subItems.map(sub => (
                      <Link
                        key={sub.key}
                        to={sub.path}
                        onClick={closeMenus}
                        className="font-overline text-sm text-jungle/70 hover:text-terracotta"
                      >
                        {t.nav[sub.key] || sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-jungle/10">
              {["en", "es", "pt"].map(l => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1.5 text-xs border font-medium uppercase tracking-widest ${
                    lang === l ? "bg-jungle text-sand border-jungle" : "border-jungle/30 text-jungle"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <Link
              to="/book"
              onClick={closeMenus}
              data-testid="mobile-header-book-btn"
              className="btn-primary mt-2 justify-center"
            >
              {t.nav.book}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}